// src/pages/ErrorPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiHome,
  FiRefreshCw,
} from "react-icons/fi";

import Reveal from "./../../components/ui/Reveal";
import { getSettings } from "./../../services/settingsServices";

function toAbsoluteUrl(url = "") {
  const value = String(url || "").trim();

  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) return `${window.location.origin}${value}`;

  return value;
}

function pickRandomIndex(length, exclude = -1) {
  if (length <= 1) return 0;

  let nextIndex = Math.floor(Math.random() * length);

  if (nextIndex === exclude) {
    nextIndex = (nextIndex + 1) % length;
  }

  return nextIndex;
}

function getErrorMessage(locationSearch) {
  const params = new URLSearchParams(locationSearch);

  return (
    params.get("error") ||
    params.get("message") ||
    "La page demandée n’a pas été trouvée ou n’est plus disponible."
  );
}

function getErrorCode(locationSearch) {
  const params = new URLSearchParams(locationSearch);

  return params.get("code") || params.get("status") || "404";
}

export default function ErrorPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const error = useMemo(() => getErrorMessage(location.search), [location.search]);
  const code = useMemo(() => getErrorCode(location.search), [location.search]);

  const [backgroundUrl, setBackgroundUrl] = useState("");
  const [loadingBackground, setLoadingBackground] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoadingBackground(true);

      try {
        const response = await getSettings();
        const settings = Array.isArray(response) ? response[0] : response;

        const urls = [
          settings?.photoURL1,
          settings?.photoURL2,
          settings?.photoURL3,
        ]
          .filter((url) => typeof url === "string" && url.trim())
          .map(toAbsoluteUrl);

        const uniqueUrls = Array.from(new Set(urls));

        if (!mounted) return;

        if (uniqueUrls.length > 0) {
          const previousIndex = Number(sessionStorage.getItem("errorBgIdx"));
          const nextIndex = pickRandomIndex(
            uniqueUrls.length,
            Number.isNaN(previousIndex) ? -1 : previousIndex
          );

          sessionStorage.setItem("errorBgIdx", String(nextIndex));
          setBackgroundUrl(uniqueUrls[nextIndex]);
        } else {
          setBackgroundUrl("");
        }
      } catch (err) {
        console.error("ErrorPage getSettings:", err);

        if (mounted) {
          setBackgroundUrl("");
        }
      } finally {
        if (mounted) {
          setLoadingBackground(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [location.key]);

  useEffect(() => {
    document.title = `Erreur ${code} — Page introuvable`;
  }, [code]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/");
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950">
      <div
        className={
          backgroundUrl
            ? "absolute inset-0 bg-cover bg-center bg-no-repeat"
            : "absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(18,119,185,0.45),transparent_34%),linear-gradient(135deg,#0f172a,#1e293b,#020617)]"
        }
        style={
          backgroundUrl
            ? {
                backgroundImage: `url(${backgroundUrl})`,
              }
            : undefined
        }
        aria-hidden="true"
      />

      <div
        className="absolute inset-0 bg-slate-950/65 backdrop-blur-[2px]"
        aria-hidden="true"
      />

      <div
        className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent"
        aria-hidden="true"
      />

      <section className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6">
        <Reveal>
          <div className="w-full max-w-2xl overflow-hidden rounded-[2rem] border border-white/15 bg-white/90 shadow-2xl backdrop-blur-xl dark:bg-slate-950/90">
            <div className="h-2 bg-gradient-to-r from-[#1277B9] via-sky-400 to-cyan-300" />

            <div className="p-6 text-center sm:p-8 md:p-10">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-red-50 text-red-600 shadow-sm dark:bg-red-500/10 dark:text-red-300">
                <FiAlertTriangle className="h-9 w-9" />
              </div>

              <p className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-[#1277B9] dark:text-sky-300">
                Erreur {code}
              </p>

              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl">
                Oups, cette page est introuvable
              </h1>

              <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-slate-600 dark:text-slate-300 md:text-base">
                {error}
              </p>

              {loadingBackground && (
                <p className="mt-4 text-xs font-semibold text-slate-400">
                  Chargement de l’arrière-plan…
                </p>
              )}

              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                >
                  <FiArrowLeft className="h-4 w-4" />
                  Revenir en arrière
                </button>

                <Link
                  to="/"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-5 text-sm font-bold text-white shadow-sm transition hover:bg-[#0f659d] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/40"
                >
                  <FiHome className="h-4 w-4" />
                  Retour à l’accueil
                </Link>

                <button
                  type="button"
                  onClick={handleReload}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                >
                  <FiRefreshCw className="h-4 w-4" />
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}